import type {
  FetchUserDataFn,
  ListWorkspaceMembershipsFn,
  GetCurrentMemberFn,
  GetCurrentUserWorkspaceMembershipsFn,
  GetTeamMembersForWorkspaceMemberFn,
  GetUserDataFn,
  PrimeUserFn,
  PrimeWorkspaceFn,
} from '@/features/iam'
import type { Member } from '@/features/iam/members/Member'
import type { User } from '@/features/iam/users/User'
import type {
  AcceptOfferFn,
  FetchBookedConsultationsFn,
  FetchProOpenJobCountsFn,
  PickupConsultationJobFn,
  PickupOpportunityFn,
} from '@/features/jobs'
import type { JobEvent } from '@/features/jobs/JobEvent'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import type { NavigateFn } from '@/RootStore'
import { createInternalFeatureLoader } from '@/utils/feature-loading-util'
import { toStream } from '@/utils/mobx-util'
import type { Id } from '@/infra/id'
import type { CursorPaged, CursorPageQuery } from '@/infra/paging'
import type { RealtimeEvent } from '@taxfyle/api-internal/internal/realtime_pb'
import { type Observable, switchMap, take } from 'rxjs'
import type { AutomatedAction } from '@/features/opportunities/offers/OpportunityOffersViewModel'

/**
 * Checks once whether the current user may view opportunities.
 */
export type ProbeCanViewOpportunitiesFn = () => Promise<boolean>

/**
 * Lists a page of the public user IDs of the pros qualified to work on the job.
 */
export type ListQualifiedProvidersFn = (
  jobId: Id,
  query: CursorPageQuery,
) => Promise<CursorPaged<Id>>

/**
 * Accepts or declines an opportunity offer on the user's behalf.
 */
export type HandleOfferResponseFn = (action: AutomatedAction) => Promise<void>

/**
 * Options for {@link OpportunitiesFeature}.
 */
export interface OpportunitiesFeatureOptions {
  token$: Observable<string>
  workRealtime$: Observable<RealtimeEvent>
  navigate: NavigateFn
  newAlert: AlertFactory
  createAlertRetryWrapper: AlertRetryWrapperFactory
  createAlertErrorHandler: AlertErrorHandlerFactory
  currentUser$: Observable<User>
  currentMember$: Observable<Member>
  getCurrentMember: GetCurrentMemberFn
  primeUser: PrimeUserFn
  primeWorkspace: PrimeWorkspaceFn
  getTeamMembersForWorkspaceMember: GetTeamMembersForWorkspaceMemberFn
  getUserData: GetUserDataFn
  fetchUserData: FetchUserDataFn
  getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn
  listWorkspaceMemberships: ListWorkspaceMembershipsFn
  pickupOpportunity: PickupOpportunityFn
  acceptOffer: AcceptOfferFn
  fetchProOpenJobCounts: FetchProOpenJobCountsFn
  fetchBookedConsultations: FetchBookedConsultationsFn
  pickupConsultationJob: PickupConsultationJobFn
  jobEvents$: Observable<JobEvent>
}

/**
 * Feature relating to opportunities, opportunity offers and workspace postings.
 */
export type OpportunitiesFeature = ReturnType<typeof OpportunitiesFeature>

export function OpportunitiesFeature(options: OpportunitiesFeatureOptions) {
  const internal = createInternalFeatureLoader(
    () => import('./internal'),
    (m) => m.InternalOpportunitiesFeature(options),
  )

  const handleOfferResponse: HandleOfferResponseFn = internal.wrap(
    (m) => m.handleOfferResponse,
  )

  const probeCanViewOpportunities: ProbeCanViewOpportunitiesFn = internal.wrap(
    (m) => m.opportunityStore.probeCanViewOpportunities,
  )

  const listQualifiedProviders: ListQualifiedProvidersFn = internal.wrap(
    (m) => m.listQualifiedProviders,
  )

  options.token$.pipe(take(1), switchMap(internal)).subscribe()

  return {
    // Internal
    internal,

    // Public
    handleOfferResponse,
    probeCanViewOpportunities,
    listQualifiedProviders,
    canViewOpportunities$: internal.defer((m) => m.canViewOpportunities$),
    systemNotifications$: internal.defer((m) => m.systemNotifications$),
    opportunityCount$: internal.deferUntilLoaded((m) =>
      toStream(() => m.opportunityStore.opportunityCount, true),
    ),
  }
}
